import React, { useMemo } from "react";

import { Box, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { makeStyles } from "tss-react/mui";

import CircularProgressBar from "@/components/AllergenModal/CircularProgressBar";
import CalorieFireIcon from "@/components/Icons/CalorieFireIcon";
import { colors } from "@/theme/overrides/colors";
import { getPortionWeightGrams } from "@/utils/food";
import { percent, round } from "@/utils/number";

import { RecipeFormValues } from "./RecipeForm";

const useStyles = makeStyles()(() => ({
  progressContainer: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 100,
    display: "flex",
    gap: 20,
    padding: "12px 24px 12px 12px",
  },
  caloriesContainer: {
    backgroundColor: colors.white,
    padding: "32px",
  },
  caloriesText: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: 28,
    color: colors.dark,
  },
  bodyText2: {
    textAlign: "center",
    fontWeight: 600,
    fontSize: 14,
    color: "#595F68",
  },
  bodyText4: {
    fontWeight: 600,
    fontSize: 16,
    color: "#595F68",
  },
  noteText: {
    fontSize: 14,
  },
}));

const Macronutrients: React.FC = () => {
  const { classes, cx } = useStyles();
  const { watch, getValues } = useFormContext<RecipeFormValues>();

  const {
    calories,
    carbohydrates,
    proteins,
    fats,
    totalCarbsGrams,
    totalProteinGrams,
    totalFatGrams,
  } = useMemo(() => calculateIngredientsMacronutrients(watch("ingredients")), [
    watch("ingredients"),
  ]);

  const selectedIngredient = watch("selectedIngredient");
  const {
    _calories,
    _carbohydrates,
    _proteins,
    _fats,
    _totalCarbsGrams,
    _totalProteinGrams,
    _totalFatGrams,
  } = calculateCurrentSelectedIngredient(
    selectedIngredient,
    watch("ingredients")
  );

  return (
    <Box>
      <Box
        alignItems="center"
        bgcolor={colors.light}
        display="flex"
        gap="24px"
        padding={2}
      >
        <Box
          className={cx(classes.progressContainer, classes.caloriesContainer)}
        >
          <Box fontSize="48px">
            <CalorieFireIcon fontSize="inherit" />
          </Box>
          <Box>
            <Typography className={classes.caloriesText}>
              {round(_calories ? _calories : calories, 4)}
            </Typography>
            <Typography className={classes.bodyText2}>Calories</Typography>
          </Box>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(
              _carbohydrates ? _carbohydrates : carbohydrates,
              { round: 0 }
            )}%`}
            label={`${_totalCarbsGrams ? _totalCarbsGrams : totalCarbsGrams}g`}
            data={[
              {
                value: percent(_carbohydrates ? _carbohydrates : carbohydrates),
                color: colors.primary,
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Carbs</Typography>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(_proteins ? _proteins : proteins, {
              round: 0,
            })}%`}
            label={`${
              _totalProteinGrams ? _totalProteinGrams : totalProteinGrams
            }g`}
            data={[
              {
                value: percent(_proteins ? _proteins : proteins),
                color: colors.secondary,
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Protein</Typography>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(_fats ? _fats : fats, { round: 0 })}%`}
            label={`${_totalFatGrams ? _totalFatGrams : totalFatGrams}g`}
            data={[
              {
                value: percent(_fats ? _fats : fats),
                color: "#2D87BB",
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Fats</Typography>
        </Box>
      </Box>
      <Box margin={0.5} />
      <Typography className={cx(classes.bodyText4, classes.noteText)}>
        Note: The calories and macronutrients are automatically generated based
        on the ingredients added.
      </Typography>
    </Box>
  );
};

/**
 * Calories are in kcal. Carbs, proteins, and fats are in percent of total ingredient weight.
 */
function calculateIngredientsMacronutrients(
  ingredientsArg: RecipeFormValues["ingredients"]
) {
  const ingredients = ingredientsArg ?? [];
  let calories = 0;
  let carbohydrates = 0;
  let proteins = 0;
  let fats = 0;
  let weight = 0;
  let totalCarbsGrams = 0;
  let totalProteinGrams = 0;
  let totalFatGrams = 0;

  ingredients.forEach((ingred) => {
    const food = ingred.food;
    const portion = ingred.foodPortion;
    const foodWeight = getPortionWeightGrams(portion, ingred.amount);
    const weightPercent = foodWeight / food.epWeightGrams;
    // Sum them up kcals and grams
    weight += foodWeight;
    calories += food.energyKcal * weightPercent;
    carbohydrates += (food.carbohydrateGrams ?? 0) * weightPercent;
    proteins += (food.proteinGrams ?? 0) * weightPercent;
    fats += (food.fatGrams ?? 0) * weightPercent;
  });

  // Calculate the macros' percentage
  totalCarbsGrams = round(carbohydrates, 3);
  totalProteinGrams = round(proteins, 3);
  totalFatGrams = round(fats, 3);

  carbohydrates = carbohydrates / (weight || 1);
  proteins = proteins / (weight || 1);
  fats = fats / (weight || 1);
  return {
    calories,
    carbohydrates,
    proteins,
    fats,
    totalCarbsGrams,
    totalProteinGrams,
    totalFatGrams,
  };
}

function calculateCurrentSelectedIngredient(
  selectedIngredient: RecipeFormValues["selectedIngredient"],
  ingredientsArr: RecipeFormValues["ingredients"]
) {
  const ingredient = selectedIngredient;
  let _calories = 0;
  let _carbohydrates = 0;
  let _proteins = 0;
  let _fats = 0;
  let _weight = 0;
  let _totalCarbsGrams = 0;
  let _totalProteinGrams = 0;
  let _totalFatGrams = 0;

  const ingredients = ingredientsArr ?? [];
  ingredients.forEach((ingred) => {
    const food = ingred.food;
    const portion = ingred.foodPortion;
    const foodWeight = getPortionWeightGrams(portion, ingred.amount);
    const weightPercent = foodWeight / food.epWeightGrams;
    _weight += foodWeight;
    _calories += food.energyKcal * weightPercent;
    _carbohydrates += (food.carbohydrateGrams ?? 0) * weightPercent;
    _proteins += (food.proteinGrams ?? 0) * weightPercent;
    _fats += (food.fatGrams ?? 0) * weightPercent;
  });

  const food = ingredient?.food ?? undefined;
  const measurement = ingredient?.measurement ?? undefined;
  const amount = ingredient?.amount ?? 0;
  if (measurement != undefined && food != undefined) {
    const foodWeight = getPortionWeightGrams(measurement, amount);
    const weightPercent = foodWeight / food.epWeightGrams;
    _weight += foodWeight;
    _calories += food.energyKcal * weightPercent;
    _carbohydrates += (food.carbohydrateGrams ?? 0) * weightPercent;
    _proteins += (food.proteinGrams ?? 0) * weightPercent;
    _fats += (food.fatGrams ?? 0) * weightPercent;
  }

  _totalCarbsGrams = round(_carbohydrates, 3);
  _totalProteinGrams = round(_proteins, 3);
  _totalFatGrams = round(_fats, 3);
  _carbohydrates = _carbohydrates / (_weight || 1);
  _proteins = _proteins / (_weight || 1);
  _fats = _fats / (_weight || 1);
  return {
    _calories,
    _carbohydrates,
    _proteins,
    _fats,
    _totalCarbsGrams,
    _totalProteinGrams,
    _totalFatGrams,
  };
}

export default Macronutrients;
